import React, {useContext, useEffect, useState} from "react";
import SuggestionForm, {SuggestionFormItem} from "../../components/forms/suggestionForm";
import {getPatientsByName} from "../patients/groupService";
import moment from "moment";
import {Language, TimeFormat} from "../../util/enum";
import {SendByActionType, sendCampaignTestMessage} from "./campaignService";
import Button from "../../components/button/button";
import {ButtonStatus} from "../../components/button/enum";
import CustomModal from "../../components/modal/customModal";
import Spinner from "../../components/spinner/spinner";
import {TemplateContent} from "./templateEditContentList";
import {replaceWithReplaceableInfo} from "./templateEditor";
import {GrayLog} from "../../grayLog/grayLog";
import {LogEventName} from "../../grayLog/logKey";
import {PromptContext} from "../promptContext";
import {GraylogContext} from "../graylogContext";
import {ProviderContext} from "../providerContext";

interface IProps {
    isShow: boolean,
    messageTemplates: Array<TemplateContent>,
    isPostAppointment: boolean,
    onCancel: () => void,
    onSend: () => void
}

export interface TestMessage {
    actionType: SendByActionType,
    title: string,
    message: string,
    email: string,
    mobilePhone: string,
    clinicId: number,
    patientId: number,
    mPatientId: number,
    RequireCheckAppointment: boolean,
    isDone: boolean
}

function SendTestingMessageModal(props: IProps) {
    const {isShow, messageTemplates, isPostAppointment, onCancel, onSend} = props
    const [sourceData, setSourceData] = useState<any>([])
    const [filterData, setFilterData] = useState<Array<SuggestionFormItem>>([])
    const [keyword, setKeyword] = useState<string>("")
    const [testMessage, setTestMessage] = useState<TestMessage | null>(null)
    const [showSpinner, setShowSpinner] = useState(false)

    const provider = useContext(ProviderContext)
    const logEmployee = useContext(GraylogContext)
    const {showError} = useContext(PromptContext)

    const getPatient = (name: string) => {
        setShowSpinner(true)
        getPatientsByName(name, (data) => {
            setShowSpinner(false)
            setSourceData(data)
            const list = data.map((_item: any) => {
                const dobDate = new Date(_item.dob)
                const dobString = moment(dobDate).format(TimeFormat.MMDDYYYY)
                const name = `${_item.firstName} ${_item.lastName} (${dobString})`
                return {
                    name: name,
                    id: _item.patientID,
                }
            })
            setFilterData(list)
        }, () => {
            setShowSpinner(false)
        })
    }

    const getPatientInfo = (id: string | number) => {
        const item = sourceData.find((_item: any) => {
            return _item.patientID === id
        })
        if (item) {
            const {email, mobilePhone, mpid, clinicID, patientID, language, firstName, lastName} = item
            let sendMsg: string | undefined
            if (["Chinese", "Chinese - China", "CHinese - Sinapore"].includes(language)) {
                sendMsg = messageTemplates.find((item) => item.language === Language.simplifiedChinese)?.compliedMessage
            } else if (["Chinese - Hong Kong SAR", "CHinese - MAcau SAR", "Chinese - Taiwan"].includes(language)) {
                sendMsg = messageTemplates.find((item) => item.language === Language.traditionalChinese)?.compliedMessage
            } else {
                sendMsg = messageTemplates.find((item) => item.language === language)?.compliedMessage
            }

            if (!sendMsg) {
                sendMsg = messageTemplates.length ? messageTemplates[0].compliedMessage : ""
            }

            sendMsg = replaceWithReplaceableInfo(sendMsg, {patientName: `${firstName} ${lastName}`, patientFirstName: firstName, practiceName: provider.clinicName})

            setTestMessage({
                actionType: SendByActionType.sms,
                email: email,
                mobilePhone: mobilePhone,
                title: "Test Message",
                message: sendMsg,
                clinicId: clinicID,
                patientId: patientID,
                mPatientId: mpid,
                RequireCheckAppointment: true,
                isDone: isPostAppointment,
            })
        }
    }

    const onChangeText = (text: string) => {
        // const isInputting = keyword.length < text.length
        //
        // if (text.length > 1) {
        //     isInputting && getPatient(text)
        // } else {
        //     setFilterData([])
        //     setTestMessage(null)
        // }
        setKeyword(text)
    }

    useEffect(() => {
        if (keyword.length > 1) {
            if (!keyword.includes(" (") && !keyword.endsWith(" ")) {
                getPatient(keyword)
            }
        } else {
            setFilterData([])
            setTestMessage(null)
        }
    }, [keyword])

    const onSendMessage = () => {
        if (testMessage) {
            setShowSpinner(true)
            sendCampaignTestMessage(testMessage, () => {
                setShowSpinner(false)
                onSend && onSend()
                GrayLog.event(LogEventName.sendTestCampaignMessage, {isSuccessful: true, ...logEmployee})
            }, (error) => {
                setShowSpinner(false)
                showError(error)
                GrayLog.event(LogEventName.sendTestCampaignMessage, {isSuccessful: false, errMsg: `${error.code} - ${error.msg}`, ...logEmployee})
            })
        }
    }

    // const $msgContentPreview = testMessage ? (
    //     <div className={"w-full flex flex-col py-4"}>
    //         <p className={"text-xs text-gray-2 font-semibold"}>SMS Content Preview</p>
    //         <div className={"mt-1 p-2 rounded border border-base-border"}>
    //             <p className={"text-sm text-gray-2"}>{testMessage.message}</p>
    //         </div>
    //     </div>
    // ) : null

    const $spinner = (<Spinner isOpen={showSpinner} />)

    return (
        <CustomModal isOpen={isShow} onClose={() => {
            setSourceData([])
            setFilterData([])
            setTestMessage(null)
            setKeyword("")
        }}>
            <div className={"p-2"}>
                <p className={"text-gray-1 font-semibold text-lg text-left"}>Send a test message</p>
                <p className={"text-gray-2 test-sm mt-5"}>Select a patient that you would like to send the test message to</p>

                <div className={"mt-5 w-96 h-80"}>
                    <p className={"text-xs text-gray-2 font-semibold mb-1"}>Patient Name</p>
                    <SuggestionForm
                        data={keyword ? filterData : []}
                        onChangeText={onChangeText}
                        onSelect={(id) => {
                            getPatientInfo(id)
                        }}
                    />
                    {/*{$msgContentPreview}*/}
                </div>

                <div className={"flex flex-row justify-end border-t border-gray-300 pt-6 grid grid-flow-col auto-cols-max gap-x-4"}>
                    <Button status={ButtonStatus.light} onClick={onCancel} >Cancel</Button>
                    <Button status={testMessage ? ButtonStatus.primary : ButtonStatus.disabled} onClick={() => {
                        onSendMessage()
                    }}>Send</Button>
                </div>
                {$spinner}
            </div>
        </CustomModal>
    )
}

export default SendTestingMessageModal
